export const SERVICES_SCHEMA = {
  en: {
    title: "What We Offer",
    sub_title:
      "Different services. One purpose: helping you understand the financial decisions shaping your life.",
    hero_ctas: [
      { text: "Create Your Account", link: "/sign-up", type: "primary_light" },
      { text: "Sign In", link: "/sign-in", type: "secondary" },
    ],
    page_nav: [
      {
        value: "all",
        label: "All",
        icon_alt: "All",
        darkIcon: "all_dark_grey.svg",
        whiteIcon: "all_white.svg",
      },
      {
        value: "services",
        label: "Services",
        icon_alt: "Services",
        darkIcon: "services_dark_grey.svg",
        whiteIcon: "services_white.svg",
      },
      {
        value: "partnership",
        label: "Partnership",
        icon_alt: "Partnership",
        darkIcon: "handshake_dark_grey.svg",
        whiteIcon: "handshake_white.svg",
      },
    ],
    offerings: [
      {
        key: "financial-insights",
        category: "services",
        title: "Financial Insights",
        cta: "Get Started",
        lottie: "financial_insights.json",
        introLink: "/sign-up",
        blocks: [
          {
            heading:
              "Financial Insights — understand the story behind your money.",
            paragraphs: [
              "Every transaction tells part of the story, but very few explain the bigger picture. Financial Insights brings your spending, recurring payments, budgets, and account activity together to help you better understand the financial patterns shaping your everyday decisions.",
            ],
          },
          {
            heading: "What this means for you",
            items: [
              "Track your spending with greater clarity.",
              "Stay on top of recurring bills and subscriptions.",
              "Build budgets that reflect your real habits.",
              "Turn everyday financial activity into meaningful insights.",
            ],
          },
        ],
        ctas: [
          { label: "Learn More", to: "/financial-insights", type: "secondary" },
          { label: "Sign In", to: "/sign-in", type: "primary" },
        ],
      },
      {
        key: "tax-filing",
        category: "services",
        title: "Tax Filing",
        cta: "Get Started",
        lottie: "tax.json",
        introLink: "/sign-up",
        blocks: [
          {
            heading: "Tax Filing — built around your tax situation.",
            paragraphs: [
              "Filing your taxes starts long before the paperwork. It starts with finding the right expert. Plaintr matches you with a certified tax professional based on your unique tax situation, so you can move forward knowing your return is in the right hands.",
            ],
          },
          {
            heading: "What this means for you",
            items: [
              "Get matched with a certified tax professional based on your needs.",
              "Know your price before any work begins.",
              "Collaborate securely from one place.",
              "Let your tax pro handle the filing from start to finish.",
            ],
          },
        ],
        ctas: [
          { label: "Learn More", to: "/tax", type: "secondary" },
          { label: "Sign In", to: "/sign-in", type: "primary" },
        ],
      },
      {
        key: "affiliate-program",
        category: "partnership",
        title: "Affiliate Program",
        cta: "Get Started",
        lottie: "crowdfunding.json",
        introLink: "/sign-up",
        blocks: [
          {
            heading:
              "Affiliate Program — built around the trust you've earned.",
            paragraphs: [
              "The recommendations people trust don't come from ads. They come from people who consistently share value. Plaintr's Affiliate Program helps you turn that trust into a new source of income while connecting your audience with financial services they can genuinely benefit from.",
            ],
          },
          {
            heading: "What this means for you",
            items: [
              "Earn recurring commissions for every qualified referral.",
              "Access ready-to-use marketing resources.",
              "Track your performance through your affiliate dashboard.",
              "Grow your income while recommending a service you believe in.",
            ],
          },
        ],
        ctas: [
          { label: "Learn More", to: "/affiliate-program", type: "secondary" },
          { label: "Sign In", to: "/sign-in", type: "primary" },
        ],
      },
    ],
    section_1: {
      intro: "Filing, Plaintr Style",
      services: [
        {
          name: "Personal Tax Filing",
          id: 0,
          accordionId: "accordion-1",
          p_content_1: [
            "You don’t need tax advice. You need a real plan.",
            "Maybe you’ve got one job. Or three. Maybe you freelance on the side, support your family, or just want to stop overpaying every year.",
            "At Plaintr, we start with your reality — not a template. You’ll get matched with a certified expert who gets the full picture and builds your filing around it.",
            "No confusion. No guesswork. Just someone who listens, files it right, and makes sure it actually works for you.",
          ],
          cta_1: {
            text: "Let’s file",
            link: "/sign-up",
          },
        },
        {
          name: "Business Tax Filing",
          id: 1,
          accordionId: "accordion-2",
          p_content_1: [
            "When you run a business, every decision counts — especially how you file.",
            "At Plaintr, we work with companies of all sizes: from single-owner operations to fast-growing teams. Whether you’re managing payroll, tracking expenses, or expanding into new states, our certified experts know how to keep your filings clean, compliant, and built for scale.",
            "We don’t offer templates. We build around your business — your structure, your growth, your goals.",
          ],
          cta_1: {
            text: "Let’s talk numbers",
            link: "/sign-up",
          },
        },
        /*
        {
          name: "Tax Planning & Expert Help",
          id: 2,
          accordionId: "accordion-3",
          p_content_1: [
            "Filing is what you do in April. Planning is what changes everything.",
            "Plaintr goes beyond the basics — we help you think ahead.",
            "Whether you’re investing in crypto, navigating equity compensation, running multiple income streams, or simply ready to be more intentional with your money — we connect you with a certified pro who helps you stay ready all year long.",
            "That means smarter deductions, fewer surprises, and a plan that’s actually built around your life — not just your tax form.",
          ],
          cta_1: {
            text: "Plan smarter",
            link: "/sign-up",
          },
        },
        */
      ],
    },
    section_2: {
      intro: "You’re not filing. You’re protecting your future.",
      p_content_1: [
        "Every year, millions of people file their taxes without really knowing what they’re doing — or what they’re missing.",
        "They settle for bots. Or overpriced firms. Or confusing DIY tools that make them feel even worse.",
        "Not you. You’re here because you want clarity. A strategy. A human who actually listens — and helps you get it right.",
        "At Plaintr, we don’t just process your numbers.",
        "We help you make smarter moves, claim what’s yours, and feel like someone finally sees the full picture.",
        "Your jobs. Your side income. Your business. Your life.",
      ],
      l_content_1: {
        intro: "This isn’t just about April.",
        items: [
          "It’s about control.",
          "It’s about confidence.",
          "It’s about time.",
        ],
      },
    },
    cta_suggestions: {
      intro: "Let’s file, the right way",
      links: [
        { text: "Sign In", link: "/sign-in", stl: "w" },
        { text: "Create Account", link: "/sign-up", stl: "w" },
        { text: "How We Match You", link: "/how-it-works", stl: "b" },
      ],
    },
  },
};
